import { Modal, Button, CheckboxWithLabel } from '@retailer-platform/shared-components'
import React, { type FunctionComponent, useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { spacing } from '@retailer-platform/shared-components'
import { useDomainMessage, useDomainMessages } from 'domains/collections/utils/domain/intl'
import { useDeleteCollection } from 'domains/collections/api/deleteCollection.hooks'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import { trackEvent } from 'utils/events/trackEvent'

interface CollectionsDeleteCollectionModalProps {
  onClose: () => void
  onSuccess: () => void
  name: string
  collectionId: string
}

const wrapper = css({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: '80vh',
})

const buttonWrapper = css({
  display: 'flex',
  alignSelf: 'flex-end',
})

const checkboxWrapper = css({
  marginTop: spacing.X24,
  marginBottom: spacing.X24,
})

export const CollectionsDeleteCollectionModal: FunctionComponent<
  CollectionsDeleteCollectionModalProps
> = ({ name, onClose, onSuccess, collectionId }) => {
  const text = useDomainMessage('collectionsDomain.delete.text', { name })
  const messages = useDomainMessages({
    title: 'collectionsDomain.delete.title',
    checkbox: 'collectionsDomain.delete.checkbox',
    cancel: 'collectionsDomain.cancel',
    submit: 'collectionsDomain.delete.submit',
    error: 'collectionsDomain.delete.error',
  })

  const [checked, setChecked] = useState(false)
  const [error, setError] = useState(false)

  const { deleteCollection, ...deleteCollectionRest } = useDeleteCollection()
  const onDelete = () => {
    trackEvent({
      id: 'collections.delete',
      description: 'Deleting a collection',
      data: { collectionId },
    })
    deleteCollection({
      collectionId,
    })
  }

  useEffect(() => {
    if (deleteCollectionRest.apiError) {
      return setError(true)
    }
  }, [deleteCollectionRest.apiError])

  useEffect(() => {
    if (deleteCollectionRest.apiSuccess) {
      onSuccess()
    }
  }, [deleteCollectionRest.apiSuccess, onSuccess])

  return (
    <Modal
      headerContent={messages.title}
      bodyContent={
        <div css={wrapper}>
          {text}
          <div css={checkboxWrapper}>
            <CheckboxWithLabel
              id="deleteCollectionCheckbox"
              label={messages.checkbox}
              checked={checked}
              onChange={() => setChecked(!checked)}
            />
          </div>
          {error && <ErrorMessage collapse={false}>{messages.error}</ErrorMessage>}
          <div css={buttonWrapper}>
            <Button variant={'detrimental'} onClick={onClose}>
              {messages.cancel}
            </Button>
            <Button variant={'detrimental'} onClick={onDelete} disabled={!checked}>
              {messages.submit}
            </Button>
          </div>
        </div>
      }
      isOpen={true}
      onRequestClose={onClose}
      maxHeight={260}
      maxWidth={500}
    />
  )
}
